import React from "react";
import { SegmentView } from "@tiny/api";
import {
  toChipColor,
  toChipText,
} from "../../AudienceList/AudienceRow/AudienceRow.helper";
import { ActionTimeInfo } from "@tiny/components/src/ItemActionTimeInfo/ItemActionTimeInfo.types";
import { useMessages } from "@tiny/components";
import { usePostExportToStaticSegment } from "@tiny/api";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { useTinyHost } from "@tiny/host-contract";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { closeSnackbar } from "notistack";

interface AudienceRowProps {
  item: SegmentView;
  reloadAudiences?: () => void;
  closeDialog: () => void;
  onSuccess?: () => void;
  mode?: "update" | "export" | "select";
  segmentIds?: string[];
}

export function useAudienceRow(props: AudienceRowProps) {
  const { item, mode = "update", segmentIds, closeDialog, onSuccess } = props;

  const { showErrorMessage } = useMessages();

  const { navigate } = useTinyHost();

  const { mutateAsync: postExportToStaticSegment, isLoading: exportLoading } =
    usePostExportToStaticSegment();

  const actions = React.useMemo<ActionTimeInfo[]>(() => {
    return [
      {
        action: "Created",
        timestamp: item.createdAt,
      },
    ];
  }, [item.createdAt]);

  const chipColor = React.useMemo(
    () => toChipColor(item.segmentType),
    [item.segmentType]
  );

  const chipText = React.useMemo(
    () => toChipText(item.segmentType),
    [item.segmentType]
  );

  const handleExportAction = React.useCallback(async () => {
    if (mode !== "export" || !segmentIds?.length) {
      return;
    }

    try {
      await postExportToStaticSegment({
        data: {
          segmentIds: segmentIds,
          existingStaticSegmentId: item.id,
        },
      });
      showUploadMessage({
        variant: "success",
        message: "Export Started",
        description: `The custom segment is being appended to [ ${item.name} ]. You can refresh the page to check the status once the process is complete.`,
        primaryCtaActionText: "Got it",
        primaryCtaActionFunction: () => {
          closeSnackbar();
        },
        persist: false,
      });
      closeDialog();
      onSuccess?.();
      navigate.to("/main/audience?tab=sources");
    } catch (e: any) {
      extractBackendError(e.response).then((be) =>
        showErrorMessage(getErrorMessage(be))
      );
    }
  }, [
    mode,
    segmentIds,
    postExportToStaticSegment,
    item.id,
    item.name,
    closeDialog,
    onSuccess,
    history,
    showErrorMessage,
  ]);

  return {
    chipColor,
    chipText,
    actions,
    handleExportAction,
    exportLoading,
  };
}
